import {
  FC,
  FocusEvent,
  useState,
  ChangeEvent,
  KeyboardEvent,
  useRef
} from "react"
import { Disclosure } from "@headlessui/react"
import { ChevronDown, X } from "lucide-react"
import { useIntegrationSettingsForm } from "../../../../hooks"
import { useIsSuperAdmin } from "../../../../hooks/use-user-roles"
import Form from "../../../form"
import { CustomParam, Integration } from "../../../../../types"

interface Props {
  integration?: Integration
}

const CUSTOM_PARAMS = [
  { key: "utm_*", scope: "lead" },
  { key: "gclid", scope: "lead" }
]

const AdvanceOptions: FC<Props> = ({ integration }) => {
  const isSuperAdmin = useIsSuperAdmin()
  const {
    saveSetting,
    handleFormikBlur,
    handleFormikChange,
    saveSettingLocal,
    values
  } = useIntegrationSettingsForm(integration)

  const [newParam, setNewParam] = useState("")
  const tagInputRef = useRef<HTMLInputElement>(null)

  const settings = values.app_settings?.settings
  const customParams: CustomParam[] = settings?.customParams ?? []
  const hasUTM = customParams.length > 0

  const onChange = (
    name: string,
    value: string | number | boolean | null | any[]
  ) => {
    saveSetting({ [name]: value })
  }

  const onChangeSettings = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name
    onChange(name, !settings?.[name as keyof typeof settings])
  }

  const isApiDisabled = !(settings?.expose_api ?? true)

  const addParam = () => {
    const key = newParam.trim()
    if (!key || customParams.some(p => p.key === key)) return
    onChange("customParams", [...customParams, { key, scope: "lead" }])
    setNewParam("")
  }

  const onTagInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addParam()
    } else if (e.key === "Backspace" && !newParam && customParams.length > 0) {
      removeParam(customParams[customParams.length - 1].key)
    }
  }

  const removeParam = (key: string) => {
    onChange(
      "customParams",
      customParams.filter(p => p.key !== key)
    )
  }

  const onBlurPOSTURL = (e: FocusEvent<HTMLInputElement>) => {
    onChange("postUrl", e.target.value)
    handleFormikBlur(e)
  }

  const onChangePOSTURL = (e: ChangeEvent<HTMLInputElement>) => {
    saveSettingLocal({ postUrl: e.target.value })
    handleFormikChange(e)
  }

  return (
    <div className="mt-4 pt-2 pb-4 border-t-zinc-200 border-t">
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="flex w-full justify-between py-2 text-left focus:outline-none text-neutral-400">
              <span>Show advanced options</span>
              <ChevronDown
                className={`${
                  open ? "rotate-180" : ""
                } h-5 w-5 transition-transform`}
              />
            </Disclosure.Button>
            <Disclosure.Panel className="py-2">
              <div className="flex items-center py-2">
                <input
                  checked={isApiDisabled}
                  onChange={() => onChange("expose_api", isApiDisabled)}
                  id="disable-api"
                  name="expose_api"
                  type="checkbox"
                  className="h-6 w-6 rounded border-neural-400 text-primary-500 focus:ring-primary-300 cursor-pointer"
                />
                <label
                  htmlFor="disable-api"
                  className="select-none ml-3 cursor-pointer"
                >
                  Disable API
                </label>
              </div>
              <div className="flex items-center py-2">
                <input
                  checked={settings?.disableCalcCards ?? false}
                  onChange={onChangeSettings}
                  id="disableCalcCards"
                  name="disableCalcCards"
                  type="checkbox"
                  className="h-6 w-6 rounded border-neural-400 text-primary-500 focus:ring-primary-300 cursor-pointer"
                />
                <label
                  htmlFor="disableCalcCards"
                  className="select-none ml-3 cursor-pointer"
                >
                  Disable Loan Calc Cards
                </label>
              </div>
              {isSuperAdmin && (
                <div className="flex items-center py-2">
                  <input
                    checked={settings?.billing_exempt ?? false}
                    onChange={onChangeSettings}
                    id="billing-exempt"
                    name="billing_exempt"
                    type="checkbox"
                    className="h-6 w-6 rounded border-neural-400 text-primary-500 focus:ring-primary-300 cursor-pointer"
                  />
                  <label
                    htmlFor="billing-exempt"
                    className="select-none ml-3 cursor-pointer"
                  >
                    Billing Exempt
                  </label>
                </div>
              )}
              <div className="pt-6">
                <Form.Input
                  inputProps={{
                    onBlur: onBlurPOSTURL,
                    onChange: onChangePOSTURL,
                    value: settings?.postUrl ?? ""
                  }}
                  label="POST URL"
                  name="postUrl"
                  placeholder="Ex. https://google.com/"
                />
              </div>
              <div className="pt-6">
                {!hasUTM ? (
                  <button
                    type="button"
                    onClick={() => onChange("customParams", CUSTOM_PARAMS)}
                    className="text-sm text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2"
                  >
                    + Add UTM Tracking
                  </button>
                ) : (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">
                        UTM / Custom Params
                      </span>
                      <button
                        type="button"
                        onClick={() => onChange("customParams", [])}
                        className="text-xs text-red-400 hover:text-red-500"
                      >
                        Remove all
                      </button>
                    </div>
                    <div
                      className="flex flex-wrap gap-1.5 rounded border border-neutral-300 px-2 py-2 cursor-text focus-within:ring-1 focus-within:ring-primary-300 focus-within:border-primary-300"
                      onClick={() => tagInputRef.current?.focus()}
                    >
                      {customParams.map(param => (
                        <span
                          key={param.key}
                          className="inline-flex items-center gap-1 rounded bg-primary-50 px-2 py-0.5 text-xs font-mono text-primary-700"
                        >
                          {param.key}
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation()
                              removeParam(param.key)
                            }}
                            className="text-primary-400 hover:text-primary-600"
                            aria-label={`Remove ${param.key}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                      <input
                        ref={tagInputRef}
                        type="text"
                        value={newParam}
                        onChange={e => setNewParam(e.target.value)}
                        onKeyDown={onTagInputKeyDown}
                        placeholder={
                          customParams.length ? "" : "utm_source, gclid…"
                        }
                        className="min-w-[8rem] flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400 border-none focus:ring-0 focus:border-none"
                      />
                    </div>
                    <p className="text-xs text-neutral-400">
                      Press Enter to add · Backspace to remove last
                    </p>
                  </div>
                )}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  )
}

export default AdvanceOptions
